import React from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom'; // RE-ADDED: BrowserRouter import
import * as Sentry from "@sentry/react";
import App from './App.tsx';
import './index.css';
import { registerSW } from 'virtual:pwa-register';

// Initialize Sentry error monitoring
Sentry.init({
  dsn: "https://25d4adf815fb85536afc6a3e155f3481@o4509530297270272.ingest.de.sentry.io/4509571077832784",
  sendDefaultPii: true,
  environment: import.meta.env.MODE,
  integrations: [
    new Sentry.BrowserTracing({
      // IMPORTANT: Adjust this to include your actual Supabase domain if you want API call tracing.
      // Example: tracePropagationTargets: ["localhost", /^https:\/\/your-supabase-project-id\.supabase\.co\//],
      tracePropagationTargets: ["localhost", /^https:\/\/example\.com\/api/], 
    }),
  ],
  tracesSampleRate: 1.0,
  autoSessionTracking: true,
  beforeSend(event, hint) {
    return event;
  },
  transport: Sentry.makeBrowserOfflineTransport(Sentry.makeFetchTransport),
  maxBreadcrumbs: 100,
  sampleRate: 1.0,
  initialScope: {
    tags: {
      component: 'mind-polish-app',
      version: '1.1.0'
    },
    user: {
      id: 'user'
    }
  }
});

// Register service worker with auto-update functionality provided by vite-plugin-pwa
const updateSW = registerSW({
  onNeedRefresh() {
    // Show update notification banner
    const updateBanner = document.createElement('div');
    updateBanner.innerHTML = `
      <div style="
        position: fixed;
        top: 1rem;
        left: 1rem;
        right: 1rem;
        background: rgba(220, 252, 231, 0.95);
        backdrop-filter: blur(8px);
        border: 1px solid rgba(187, 247, 208, 0.5);
        border-radius: 1.5rem;
        padding: 1rem;
        z-index: 1000;
        text-align: center;
        font-family: Inter, system-ui, sans-serif;
        color: #22c55e;
        font-weight: 300;
        box-shadow: 0 4px 6px rgba(0,0,0,0.1);
        max-width: 400px;
        margin: 0 auto;
      ">
        <p style="margin: 0 0 0.75rem 0; font-size: 0.875rem;">
          🌱 App updated with latest improvements
        </p>
        <button id="update-app" style="
          background: #bbf7d0;
          border: none;
          padding: 0.5rem 1rem;
          border-radius: 1rem;
          color: #22c55e;
          font-weight: 300;
          font-size: 0.875rem;
          cursor: pointer;
          margin-right: 0.5rem;
        ">
          Apply now
        </button>
        <button id="dismiss-update" style="
          background: transparent;
          border: none;
          padding: 0.5rem 1rem;
          color: #4ade80;
          font-weight: 300;
          font-size: 0.875rem;
          cursor: pointer;
        ">
          Later
        </button>
      </div>
    `;
    document.body.appendChild(updateBanner);

    // Add event listeners for the banner buttons
    updateBanner.querySelector('#update-app')?.addEventListener('click', () => {
      updateSW(true); // Call updateSW to force service worker update
      updateBanner.remove(); // Remove banner after action
    });
    updateBanner.querySelector('#dismiss-update')?.addEventListener('click', () => {
      updateBanner.remove(); // Remove banner if dismissed
    });
  },
  onOfflineReady() {
    console.log('App ready to work offline');
  },
  immediate: true // Register and activate the SW as soon as possible
});

// Define and Initialize haptic feedback and other PWA utilities
// It's good practice to define this function higher up or in its own utility file
// if it's meant to be global or used by other modules.
const initializePWAUtilities = () => {
  if (typeof window !== 'undefined') { // Ensure window is defined (for SSR safety, though likely not an issue here)
    // Create global PWA utilities object on window
    (window as any).mindPolishPWA = { // Use 'as any' or define a custom Window interface for type safety
      haptic: {
        gentle: () => {
          if ('vibrate' in navigator) {
            navigator.vibrate(50);
          }
        },
        success: () => {
          if ('vibrate' in navigator) {
            navigator.vibrate([100, 50, 100]);
          }
        },
        notification: () => {
          if ('vibrate' in navigator) {
            navigator.vibrate([200, 100, 200, 100, 200]);
          }
        },
        error: () => {
          if ('vibrate' in navigator) {
            navigator.vibrate([300, 100, 300]);
          }
        }
      },
      network: {
        isOnline: () => navigator.onLine,
        onStatusChange: (callback: (isOnline: boolean) => void) => { // Added type for clarity
          window.addEventListener('online', () => callback(true));
          window.addEventListener('offline', () => callback(false));
        }
      }
    };
  }
};

// Initialize PWA utilities when the script runs
initializePWAUtilities();


// Render Your React App
ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    {/* BrowserRouter must wrap all components that use React Router hooks,
        including Sentry.ErrorBoundary and App (which likely contains ThemeProvider and AuthProvider). */}
    <BrowserRouter> {/* RE-ADDED: BrowserRouter here */}
      <Sentry.ErrorBoundary
        fallback={({ error, resetError }) => (
          // Wrapped the entire fallback content in a React.Fragment to resolve "Adjacent JSX elements" error.
          <>
            <div className="min-h-screen flex items-center justify-center bg-sage-25 dark:bg-sage-900 px-8">
              <div className="bg-white dark:bg-sage-800 rounded-3xl border border-sage-100/50 dark:border-sage-700/50 p-8 max-w-md w-full text-center">
                <div className="w-12 h-12 mx-auto mb-6 bg-peach-100/60 dark:bg-peach-900/30 rounded-full flex items-center justify-center">
                  <svg className="w-6 h-6 text-peach-500 dark:text-peach-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
                  </svg>
                </div>
                <h2 className="text-sage-600 dark:text-sage-200 font-light text-lg mb-4">
                  Something went gently wrong
                </h2>
                <p className="text-sage-500 dark:text-sage-300 font-light text-sm leading-relaxed mb-6">
                  We've been notified and are looking into it. Your peaceful space will be restored soon.
                </p>
                <div className="space-y-3">
                  <button
                    onClick={resetError}
                    className="w-full px-6 py-3 bg-sage-100/60 hover:bg-sage-200/50
                                 dark:bg-sage-700/60 dark:hover:bg-sage-600/50
                                 text-sage-600 dark:text-sage-200 rounded-2xl text-sm font-light
                                 border border-sage-200/40 dark:border-sage-600/40
                                 hover:border-sage-300/50 dark:hover:border-500/50
                                 transition-colors duration-300 focus:outline-none focus:ring-4 focus:ring-sage-100/50 dark:focus:ring-sage-500/30"
                  >
                    Try again
                  </button>
                  <button
                    onClick={() => window.location.href = '/'}
                    className="w-full px-6 py-3 bg-cream-50/60 hover:bg-cream-100/50
                                 dark:bg-cream-900/20 dark:hover:bg-cream-800/30
                                 text-cream-600 dark:text-cream-300 rounded-2xl text-sm font-light
                                 border border-cream-100/40 dark:border-cream-700/30
                                 hover:border-cream-200/50 dark:hover:border-600/40
                                 transition-colors duration-300 focus:outline-none focus:ring-4 focus:ring-cream-100/50 dark:focus:ring-cream-700/30"
                  >
                    Return home
                  </button>
                </div>
              </div>
            </div>
          </>
        )}
        showDialog={false}
        beforeCapture={(scope, error, errorInfo) => {
          scope.setTag('errorBoundary', true);
          scope.setContext('errorInfo', {
            value: errorInfo,
            message: error?.message || '',
            stack: error?.stack || '',
            name: error?.name || 'UnknownError'
          });
        }}
      >
        <App />
      </Sentry.ErrorBoundary>
    </BrowserRouter> {/* RE-ADDED: BrowserRouter here */}
  </React.StrictMode>,
);
